import { AnalyticsBuilderFieldOption, ModuleBaseModel, ModuleBasePropsModel } from '@/types';

// One node of a Build a Report facet tree. `value` is what gets selected and
// written into the prompt: the plain name, or — when the same name appears in
// several branches (e.g. "Others") — prefixed with enough ancestors to be
// unique ("Beer › Others"). Omitted when it equals `name`, to keep page props small.
export interface NovaTaxonomyOption {
  id: string;
  name: string;
  value?: string;
  options?: NovaTaxonomyOption[];
}

// Facet trees for Nova's "Build a Report" dropdowns, sourced from the CMS
// /tools/nova-ai page instead of hardcoded lists.
export interface NovaTaxonomy {
  industries: NovaTaxonomyOption[];
  regions: NovaTaxonomyOption[];
  generations: NovaTaxonomyOption[];
  topics: NovaTaxonomyOption[];
}

// NovaLandingModule's props carry each facet as a list of taxonomy options
// (`{ id, name, key, options? }`, same as Analytics Builder's). `industries` is a
// deep tree (sector → sub-sector → …); the others are currently flat.
export type NovaLandingModuleProps = ModuleBasePropsModel &
  Partial<Record<keyof NovaTaxonomy, AnalyticsBuilderFieldOption[]>>;

export const NOVA_TAXONOMY_PAGE_URL = '/tools/nova-ai';

export const PATH_SEPARATOR = ' › ';

export const emptyNovaTaxonomy = (): NovaTaxonomy => ({ industries: [], regions: [], generations: [], topics: [] });

export const optionValue = (option: NovaTaxonomyOption) => option.value ?? option.name;

// The CMS may send module names camelCased (CMSModule upper-cases the first
// letter before lookup), so match case-insensitively.
export const isNovaLandingModule = (moduleName?: string) => moduleName?.toLowerCase() === 'novalandingmodule';

type PathedOption = { option: NovaTaxonomyOption; path: string[] };

// Strips the CMS options down to id/name/children, then disambiguates names
// that repeat within the facet by prefixing the shortest unique ancestor path.
const toFacetTree = (source?: AnalyticsBuilderFieldOption[]): NovaTaxonomyOption[] => {
  const all: PathedOption[] = [];

  const convert = (options: AnalyticsBuilderFieldOption[] = [], ancestors: string[]): NovaTaxonomyOption[] =>
    options
      .map((source) => ({ source, name: source.name?.trim() }))
      .filter(({ name }) => !!name)
      .map(({ source, name }) => {
        const option: NovaTaxonomyOption = { id: String(source.id), name };
        const children = convert(source.options, [...ancestors, name]);
        if (children.length) option.options = children;
        all.push({ option, path: [...ancestors, name] });
        return option;
      });

  const tree = convert(source, []);

  const byName = new Map<string, PathedOption[]>();
  all.forEach((entry) => byName.set(entry.option.name, [...(byName.get(entry.option.name) ?? []), entry]));

  byName.forEach((entries) => {
    if (entries.length < 2) return;
    const labelFor = (entry: PathedOption, depth: number) => entry.path.slice(-depth).join(PATH_SEPARATOR);
    entries.forEach((entry) => {
      let depth = 2;
      while (
        depth < entry.path.length &&
        entries.some((other) => other !== entry && labelFor(other, depth) === labelFor(entry, depth))
      ) {
        depth += 1;
      }
      entry.option.value = labelFor(entry, depth);
    });
  });

  return tree;
};

export const extractNovaTaxonomy = (
  pageData?: { moduleContent?: ModuleBaseModel<ModuleBasePropsModel>[] | null } | null,
): NovaTaxonomy => {
  const props = (
    pageData?.moduleContent?.find((module) => isNovaLandingModule(module.moduleName)) as
      | ModuleBaseModel<NovaLandingModuleProps>
      | undefined
  )?.props;

  return {
    industries: toFacetTree(props?.industries),
    regions: toFacetTree(props?.regions),
    generations: toFacetTree(props?.generations),
    topics: toFacetTree(props?.topics),
  };
};
